import { useEffect } from "react";

interface SEOProps {
  title: string;
  description: string;
  keywords?: string;
  ogImage?: string;
  noindex?: boolean;
  schema?: Record<string, unknown> | Record<string, unknown>[];
}

function getSiteUrl(): string {
  return import.meta.env.VITE_SITE_URL || "";
}

function getCurrentPath(): string {
  if (typeof window !== "undefined") {
    return window.location.pathname;
  }
  return "";
}

export default function SEO({
  title,
  description,
  keywords,
  ogImage,
  noindex,
  schema,
}: SEOProps) {
  const siteUrl = getSiteUrl();
  const currentUrl = siteUrl ? siteUrl + getCurrentPath() : "";

  useEffect(() => {
    const scriptId = "page-jsonld";
    if (schema) {
      let script = document.getElementById(scriptId) as HTMLScriptElement | null;
      if (!script) {
        script = document.createElement("script");
        script.id = scriptId;
        script.type = "application/ld+json";
        document.head.appendChild(script);
      }
      script.textContent = JSON.stringify(schema);
    } else {
      const existing = document.getElementById(scriptId);
      if (existing) existing.remove();
    }

    return () => {
      const existing = document.getElementById(scriptId);
      if (existing) existing.remove();
    };
  }, [schema]);

  return (
    <>
      <title>{title}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      {noindex && <meta name="robots" content="noindex, nofollow" />}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content="website" />
      {currentUrl && (
        <>
          <meta property="og:url" content={currentUrl} />
          <link rel="canonical" href={currentUrl} />
        </>
      )}
      {ogImage && <meta property="og:image" content={ogImage} />}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      {ogImage && <meta name="twitter:image" content={ogImage} />}
    </>
  );
}